import { ChannelsName, ChannelsType } from '@/enums';
import { t } from 'i18next';
import * as Yup from 'yup';
import { INVALID_FIELDS, YupString, YupStringWithoutRequired } from './general';
import { ChannelName } from '@/enums/channel';

/* -------------------------- Old validation schema ------------------------- */
export const createOrderSchema = Yup.object().shape({
  orderId: Yup.string(),
  orderDate: Yup.string(),
  channelId: YupString.nullable(),
  projectId: YupString.nullable(),
  partnerName: YupString,
  invoiceNo: YupString,
  notes: YupString,
  donorInfoData: Yup.object().shape({
    email: Yup.string()
      .nullable()
      .email('validation.messages.wrongEmailFormat'),
  }),
  products: Yup.array()
    .of(
      Yup.object().shape({
        productId: YupString.nullable(),
        quantity: Yup.number()
          .min(1, (INVALID_FIELDS.MIN_NUMBER, { number: 1 }))
          .required(INVALID_FIELDS.REQUIRED),
        price: Yup.number().min(0),
        unitPrice: Yup.number()
          .min(0, (INVALID_FIELDS.MIN_NUMBER, { number: 1 }))
          .required(INVALID_FIELDS.REQUIRED),
      }),
    )
    .test('unique', 'duplicateKit', function (list, context) {
      const listKitSerial = (
        list?.reduce((pre: any, curr: any) => {
          return [...pre, ...curr?.kitSerials];
        }, []) || []
      )?.filter((item: string) => item?.length);

      return listKitSerial?.length === new Set(listKitSerial).size;
    })
    .required('validation.messages.mustHaveProduct')
    .min(1, 'minimumOfOneProduct'),
  // external: Yup.array()
  //   .of(
  //     Yup.object({
  //       serial: Yup.string(),
  //     }),
  //   )
  //   .nullable()
  //   .test('require', 'validation.messages.required', value => {
  //     if (!value) return true;
  //     const result = value.some(item => !!!item.serial);
  //     return !result;
  //   }),
  // .test('Unique', 'validation.messages.serialMustBeUnique', value => {
  //   if (!value) return true;
  //   return (
  //     value?.length ===
  //     [...new Map(value?.map(item => [item.serial, item])).values()].length
  //   );
  // }),
});
/* ------------------------------------ # ----------------------------------- */

//

/* -------------------------------------------------------------------------- */
/*                         New Order Validation Schema                        */
/* -------------------------------------------------------------------------- */

//#region start New Order Validation Schema
// For AMD channel
const amdSchema = Yup.object().shape({
  firstName: YupStringWithoutRequired,
  lastName: YupStringWithoutRequired,
  responseId: YupStringWithoutRequired,
  gender: YupStringWithoutRequired,
  email: Yup.string().nullable().email('validation.messages.wrongEmailFormat'),
});

// For External channel
const externalCreateSchema = {
  channelId: YupString,
  codes: Yup.array().of(YupString).optional(),
  projectId: YupString,
  projects: Yup.array().of(
    Yup.object().shape({
      projectId: YupString,
      products: Yup.array().of(
        Yup.object().shape({
          productId: YupString,
          quantity: YupString.test('positive', 'Positive number', value => {
            if (+value <= 0) return false;
            return true;
          }),
        }),
      ),
    }),
  ),
};

const productOrderShapeSchema = {
  productId: YupString,
  quantity: YupString.test('positive', 'Positive number', value => {
    if (+value <= 0) return false;
    return true;
  }),
  productItems: Yup.array().of(
    Yup.object().shape({
      shouldCheckSerial: Yup.boolean(),
      shouldCheckProbiotic: Yup.boolean(),
      serials: Yup.array()
        .nullable()
        .when('shouldCheckSerial', {
          is: true,
          then: () =>
            Yup.array()
              .nullable()
              .of(
                YupString.matches(
                  /^[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}$/,
                  'Wrong format',
                ),
              ),
        }),
      bottleId: Yup.string().when('shouldCheckBottleId', {
        is: true,
        then: () => YupString.required('Required'),
      }),
    }),
  ),
};

// For Others (default) channel
const baseFieldsSchema = {
  channelId: YupString,
  codes: Yup.array().of(YupString).optional(),
  projectId: YupString, //For non-Research channel
  projectIds: Yup.array() //For all channels
    .of(YupString)
    .min(1, ({ min }) => t(INVALID_FIELDS.MIN_NUMBER, { number: min })),
  projects: Yup.array().of(
    Yup.object().shape({
      projectId: YupString,
      products: Yup.array().of(Yup.object().shape(productOrderShapeSchema)),
    }),
  ),
};

const specificChannelSchema: Record<string, Yup.ObjectShape> = {
  [ChannelsName[ChannelsType.AMD]]: { donorInfoData: amdSchema },
  [ChannelsName[ChannelsType.EXTERNAL]]: {
    invoiceNo: YupStringWithoutRequired,
  },
};

export const newCreateOrderSchema = (channelName: string) => {
  if (channelName === ChannelsName[ChannelsType.AMILI_HEALTH]) {
    return Yup.object().shape(baseFieldsSchema);
  }

  const isExternal = channelName === ChannelName.EXTERNAL;
  const schemaByChannel = isExternal ? externalCreateSchema : baseFieldsSchema;
  const schema = Yup.object().shape({
    ...schemaByChannel,
    notes: YupStringWithoutRequired,
  });

  // if (channelName === ChannelsName[ChannelsType.EXTERNAL]) {
  //   schema = Yup.object().shape({
  //     ...nullableProductItemsSchema,
  //     notes: YupStringWithoutRequired,
  //   });
  // }

  if (specificChannelSchema[channelName]) {
    return schema.shape(specificChannelSchema[channelName]);
  }

  return schema.shape({
    partnerName: YupStringWithoutRequired,
    invoiceNo: YupStringWithoutRequired,
  });
};

export const generateKitSerialValidationSchema = Yup.object().shape({
  quantity: Yup.number().required(INVALID_FIELDS.REQUIRED).min(1),
  prefix: Yup.string()
    .nullable()
    .max(4)
    .matches(/^[^iI1lLoO0\W\s_]+$/gm, INVALID_FIELDS.PREFIX_INVALID),
});

export const restrictValidationSchema = Yup.object().shape({
  prefix: Yup.string()
    .required(INVALID_FIELDS.REQUIRED)
    .matches(/^[^iI1lLoO0\W\s_]+$/gm, INVALID_FIELDS.PREFIX_INVALID),
});

//#region start Update Order Validation Schema
const updateProductsSchema = Yup.array().of(
  Yup.object().shape({
    productId: YupString,
    codes: Yup.array().of(YupString).optional(),
    shouldCheckBottleId: Yup.boolean(),
    quantity: YupString.test('positive', 'Positive number', value => {
      if (+value <= 0) return false;
      return true;
    }),
    productItems: Yup.array().of(
      Yup.object().shape({
        serials: Yup.array()
          .nullable()
          .of(
            YupString.matches(
              /^[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}$/,
              'Wrong format',
            ),
          ),
        probiotics: Yup.array().nullable().of(YupString),
        bottleId: Yup.string().when('shouldCheckBottleId', {
          is: true,
          then: () => YupString.required('Required'),
        }),
      }),
    ),
  }),
);
const amiliHealthUpdateSchema = Yup.object().shape({
  projectId: YupString,
  projects: Yup.array().of(
    Yup.object().shape({
      projectId: YupString,
      products: updateProductsSchema,
    }),
  ),
});

const externalUpdateSchema = Yup.object().shape({
  projectId: YupString,
  projects: Yup.array().of(
    Yup.object().shape({
      projectId: YupString,
      products: Yup.array().of(
        Yup.object().shape({
          productId: YupString,
          quantity: YupString.test('positive', 'Positive number', value => {
            if (+value <= 0) return false;
            return true;
          }),
        }),
      ),
    }),
  ),
});

const researchUpdateSchema = Yup.object().shape({
  channelId: YupString,
  projectId: YupString,
  projects: Yup.array().of(
    Yup.object().shape({
      projectId: YupString,
      products: updateProductsSchema,
    }),
  ),
});

export const defaultUpdateSchema = amiliHealthUpdateSchema.shape({
  notes: YupStringWithoutRequired.nullable(),
});
export const updateOrderSchema = {
  [ChannelName.AMILI_HEALTH]: amiliHealthUpdateSchema,
  [ChannelName.EXTERNAL]: externalUpdateSchema,
  [ChannelName.RESEARCH]: researchUpdateSchema,
};
